import { StatusPill } from '@bb/ui';
import { Fragment } from 'react';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** A JSON body pretty-printed; anything else as it is. */
const prettyBody = (body: string) => {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
};

/**
 * «View» of a previewed webhook (BB-142): the request line, the headers as sent (secret header values and the
 * signature are masked: they are computed at send time), the body and the typed params.
 */
export function WebhookRequestView({ payload, masked }: { payload: unknown; masked: boolean }) {
  if (!isObject(payload)) return <p className="hint">No request to show: see the payload JSON.</p>;
  const endpoint = isObject(payload.endpoint) ? payload.endpoint : {};
  const headers = isObject(payload.headers) ? Object.entries(payload.headers) : [];
  const params = isObject(payload.params) ? Object.entries(payload.params) : [];
  return (
    <div className="payload-preview__view webhook-request" aria-label="Webhook request">
      <p>
        <code className="webhook-request__line">
          {String(payload.method ?? '')} {String(payload.url ?? '')}
        </code>{' '}
        {endpoint.enabled === false && <StatusPill tone="warning">Endpoint disabled</StatusPill>}
      </p>
      {typeof endpoint.name === 'string' && <p className="hint">Endpoint {endpoint.name}</p>}
      <section aria-label="Request headers">
        <h4>Headers</h4>
        <dl className="facts">
          {headers.map(([name, value]) => (
            <Fragment key={name}>
              <dt>{name}</dt>
              <dd>
                <code>{String(value)}</code>
              </dd>
            </Fragment>
          ))}
        </dl>
        <p className="hint">Secret header values and the signature show as •••: the service adds them when it sends.</p>
      </section>
      <section aria-label="Request body">
        <h4>Body</h4>
        <pre className="sdk-log">{typeof payload.body === 'string' ? prettyBody(payload.body) : ''}</pre>
      </section>
      {params.length > 0 && (
        <section aria-label="Typed params">
          <h4>Params</h4>
          <dl className="facts">
            {params.map(([name, value]) => (
              <Fragment key={name}>
                <dt>{name}</dt>
                <dd>
                  <code>{JSON.stringify(value)}</code>
                </dd>
              </Fragment>
            ))}
          </dl>
        </section>
      )}
      {masked && <p className="hint">Personal data shows as •••.</p>}
    </div>
  );
}
